import React, { useEffect, useState } from 'react';
import { useSiteBrand } from '../../contexts/SiteBrandContext.js';

const RAIL_KEY = 'flare_social_rail';

const ICONS = {
  facebook: (
    <path d="M14 8.2h2.4V5.1h-2.4C12 5.1 10.4 6.8 10.4 8.9V11H8v3.2h2.4V22h3.3v-7.8H16l.5-3.2h-2.8V9.2c0-.6.4-1 .8-1z" fill="currentColor" />
  ),
  instagram: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4.1" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.2" cy="6.8" r="1.05" fill="currentColor" />
    </>
  ),
  youtube: (
    <path d="M21.6 7.2a2.7 2.7 0 00-1.9-1.9C18.1 5 12 5 12 5s-6.1 0-7.7.3A2.7 2.7 0 002.4 7.2 28 28 0 002 12a28 28 0 00.4 4.8 2.7 2.7 0 001.9 1.9C5.9 19 12 19 12 19s6.1 0 7.7-.3a2.7 2.7 0 001.9-1.9A28 28 0 0022 12a28 28 0 00-.4-4.8zM10 15.1V8.9L15.6 12 10 15.1z" fill="currentColor" />
  ),
  twitter: (
    <path d="M4.2 4h4.4l3.2 4.4L15.7 4H20l-6.1 7.1L20.2 20h-4.5l-3.6-5L7.9 20H4l6.5-7.5L4.2 4zm3.3 1.6l8.9 12.8h1.6L9.1 5.6H7.5z" fill="currentColor" />
  ),
  whatsapp: (
    <path
      fill="currentColor"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
    />
  ),
};

export const SOCIAL_NETWORKS = [
  { key: 'socialFacebook', label: 'Facebook', bg: '#1877F2', icon: 'facebook' },
  { key: 'socialInstagram', label: 'Instagram', bg: 'linear-gradient(135deg,#f58529,#dd2a7b 50%,#8134af)', icon: 'instagram' },
  { key: 'socialYoutube', label: 'YouTube', bg: '#FF0000', icon: 'youtube' },
  { key: 'socialTwitter', label: 'X', bg: '#111111', icon: 'twitter' },
  { key: 'waHref', label: 'WhatsApp', bg: '#25D366', icon: 'whatsapp' },
];

export function brandSocialLinks(brand) {
  return SOCIAL_NETWORKS
    .map((n) => ({ ...n, href: brand?.[n.key] }))
    .filter((n) => n.href && n.href !== '#');
}

export function BrandSocialIcon({ network, size = 36, className = '' }) {
  const px = `${size}px`;
  const icon = Math.round(size * (network.icon === 'whatsapp' ? 0.72 : 0.52));
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full text-white shadow-sm ${className}`}
      style={{ width: px, height: px, background: network.bg }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width={icon} height={icon}>{ICONS[network.icon]}</svg>
    </span>
  );
}

function whatsappChatHref(href) {
  if (!href || href === '#') return '';
  if (/[?&]text=/.test(href)) return href;
  const join = href.includes('?') ? '&' : '?';
  return `${href}${join}text=${encodeURIComponent('Hi Flare Magazine, I need some help.')}`;
}

export function ShopFloatWidgets() {
  const brand = useSiteBrand();
  const links = brandSocialLinks(brand);
  const wa = whatsappChatHref(brand.waHref);
  const [railOpen, setRailOpen] = useState(() => {
    try { return localStorage.getItem(RAIL_KEY) !== '0'; } catch (e) { return true; }
  });
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 360);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toggleRail = () => {
    setRailOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(RAIL_KEY, next ? '1' : '0'); } catch (e) {}
      return next;
    });
  };

  const socialOnly = links.filter((n) => n.key !== 'waHref');
  const railLinks = [
    ...socialOnly,
    ...(wa ? [{ key: 'waHref', label: 'WhatsApp', bg: '#25D366', icon: 'whatsapp', href: wa }] : []),
  ];

  return (
    <>
      <style>{`
        @keyframes flare-wa-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-7px); }
        }
        @keyframes flare-wa-ring {
          0% { transform: scale(1); opacity: 0.45; }
          100% { transform: scale(1.7); opacity: 0; }
        }
        .flare-wa-fab:hover .flare-wa-btn { animation-play-state: paused; transform: scale(1.06); }
      `}</style>

      {wa ? (
        <a
          href={wa}
          target="_blank"
          rel="noreferrer"
          aria-label="Chat on WhatsApp"
          title="Chat on WhatsApp"
          className="flare-wa-fab fixed z-[70] left-4 sm:left-6 bottom-5 sm:bottom-6 group"
        >
          <span className="absolute inset-0 rounded-full bg-[#25D366]" style={{ animation: 'flare-wa-ring 2s ease-out infinite' }} />
          <span className="absolute inset-0 rounded-full bg-[#25D366]" style={{ animation: 'flare-wa-ring 2s ease-out infinite 0.7s' }} />
          <span
            className="flare-wa-btn relative flex items-center justify-center w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] rounded-full text-white ring-2 ring-white/30"
            style={{
              background: 'linear-gradient(180deg, #2BE36A 0%, #25D366 55%, #1EBE5A 100%)',
              boxShadow: '0 10px 28px rgba(37, 211, 102, 0.48), 0 2px 6px rgba(16, 185, 129, 0.35)',
              animation: 'flare-wa-bounce 2s ease-in-out infinite',
            }}
          >
            <svg viewBox="0 0 24 24" className="w-9 h-9 sm:w-10 sm:h-10" aria-hidden="true">{ICONS.whatsapp}</svg>
          </span>
          <span className="hidden lg:flex absolute left-full ml-3 top-1/2 -translate-y-1/2 whitespace-nowrap px-3 py-1.5 rounded-full bg-gray-900 text-white text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
            Chat on WhatsApp
          </span>
        </a>
      ) : null}

      <div className="hidden lg:flex fixed z-[70] right-5 top-1/2 -translate-y-1/2 flex-col items-center">
        {railOpen ? (
          <div className="flex flex-col items-center gap-2.5 py-3 px-2 rounded-full bg-white/95 backdrop-blur-md border border-gray-100 shadow-[0_12px_40px_rgba(15,23,42,0.14)]">
            <button
              type="button"
              onClick={toggleRail}
              className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              aria-label="Hide social bar"
              title="Hide"
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.2">
                <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            {railLinks.map((n) => (
              <a
                key={n.key}
                href={n.href}
                target="_blank"
                rel="noreferrer"
                aria-label={n.label}
                title={n.label}
                className="transition-transform hover:-translate-y-0.5 hover:scale-110"
              >
                <BrandSocialIcon network={n} size={38} />
              </a>
            ))}
          </div>
        ) : (
          <button
            type="button"
            onClick={toggleRail}
            className="w-11 h-11 rounded-full bg-white border border-gray-100 shadow-[0_10px_28px_rgba(15,23,42,0.14)] flex items-center justify-center text-gray-700 hover:text-[#c4151c] transition-colors"
            aria-label="Show social bar"
            title="Social"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        aria-label="Back to top"
        title="Back to top"
        className={`fixed z-[70] right-4 sm:right-5 bottom-5 sm:bottom-6 w-12 h-12 rounded-full text-white shadow-[0_10px_24px_rgba(196,21,28,0.35)] flex items-center justify-center transition-all ${
          showTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3 pointer-events-none'
        }`}
        style={{ backgroundColor: '#c4151c' }}
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.4">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
        </svg>
      </button>
    </>
  );
}

export default ShopFloatWidgets;
